import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type {
  GraphEdge,
  GraphLens,
  GraphNode,
  OntologyItem,
  ViewportCommand,
} from "../types";

const TOPIC_COLORS = ["#5c8ef5", "#a48ae8", "#e1912f", "#69b68d", "#e05a5f", "#8fa3b8"];
const DEFAULT_ROTATION = { x: -0.14, y: -0.38 };
const INITIAL_ZOOM = 1.18;
const MAX_AMBIENT_LABELS = 6;
const MAX_MOBILE_AMBIENT_LABELS = 3;
const MIN_ZOOM = 0.55;
const MAX_ZOOM = 2.3;
const COSMIC_FRAME_INTERVAL = 1000 / 30;
const AMBIENT_EDGE_ALPHA = 0.06;
const NEUTRAL_EDGE_COLOR = "#737876";
const STAR_GLOW = {
  ambientRadius: 6.3,
  ambientStrength: 0.38,
  coreRadius: 0.62,
  selectedRadius: 9.2,
};

const EDGE_STYLES: Record<string, { dash: number[]; width: number }> = {
  requires: { dash: [], width: 1.05 },
  supports: { dash: [4, 4], width: 0.78 },
  extends: { dash: [4, 4], width: 0.92 },
  applies_to: { dash: [2, 4], width: 0.84 },
  mitigates: { dash: [6, 4], width: 0.9 },
  contrasts_with: { dash: [2, 3], width: 1 },
};
const DEFAULT_EDGE_STYLE = { dash: [1, 4], width: 0.78 };

interface NormalizedPoint {
  x: number;
  y: number;
  z: number;
}

interface ScreenNode {
  alpha: number;
  color: string;
  node: GraphNode;
  radius: number;
  x: number;
  y: number;
  z: number;
}

interface DragState {
  lastX: number;
  lastY: number;
  moved: boolean;
  pointerId: number;
  startX: number;
  startY: number;
}

interface BackgroundStar {
  alpha: number;
  band: number;
  phase: number;
  size: number;
  x: number;
  y: number;
}

interface GraphCanvasProps {
  autoRotate: boolean;
  edges: GraphEdge[];
  edgeTypes: OntologyItem[];
  highlightedEdgeType: string | null;
  hasActiveFilters: boolean;
  focusNodeIds: Set<string>;
  hasSearch: boolean;
  lens: GraphLens;
  nodes: GraphNode[];
  nodeTypes: OntologyItem[];
  origins?: OntologyItem[];
  reducedMotion: boolean;
  searchMatchIds: Set<string>;
  selectedNodeId: string | null;
  viewportCommand: ViewportCommand;
  onClearEdgeTypeHighlight: () => void;
  onSelect: (nodeId: string | null) => void;
  onReset: () => void;
}

function clamp(value: number, minimum: number, maximum: number) {
  return Math.min(maximum, Math.max(minimum, value));
}

function normalizedScore(value: number) {
  const score = Number.isFinite(value) ? value : 0;
  if (score <= 1) return clamp(score, 0, 1);
  if (score <= 5) return clamp(score / 5, 0, 1);
  return clamp(score / 100, 0, 1);
}

function hashString(value: string) {
  let hash = 0;
  for (let index = 0; index < value.length; index += 1) {
    hash = (hash * 31 + value.charCodeAt(index)) | 0;
  }
  return Math.abs(hash);
}

function milkyWayCenterY(x: number) {
  return 0.86 - x * 0.7;
}

function milkyWayStrength(x: number, y: number) {
  const distance = Math.abs(y - milkyWayCenterY(x));
  return Math.exp(-(distance * distance) / (2 * 0.17 * 0.17));
}

function createBackgroundStars(count: number) {
  let seed = 0x51_48_2a_7d;
  const random = () => {
    seed = (Math.imul(seed, 1_664_525) + 1_013_904_223) >>> 0;
    return seed / 0x1_00_00_00_00;
  };

  return Array.from({ length: count }, (): BackgroundStar => {
    const x = random();
    const isBandStar = random() < 0.72;
    const spread = 0.035 + random() * 0.18;
    const offset = (random() + random() - 1) * spread;
    const y = isBandStar ? clamp(milkyWayCenterY(x) + offset, 0.01, 0.99) : random();
    const band = milkyWayStrength(x, y);
    const standout = random() < 0.025;

    return {
      alpha: isBandStar ? 0.018 + random() * 0.085 : 0.008 + random() * 0.04,
      band,
      phase: random() * Math.PI * 2,
      size: standout ? 0.42 + random() * 0.34 : 0.09 + random() * (band > 0.45 ? 0.42 : 0.28),
      x,
      y,
    };
  });
}

const BACKGROUND_STARS = createBackgroundStars(280);
const COLOR_CACHE = new Map<string, { blue: number; green: number; red: number }>();

function colorToRgb(color: string) {
  const cached = COLOR_CACHE.get(color);
  if (cached) return cached;

  const normalized = color.replace("#", "");
  const expanded =
    normalized.length === 3
      ? normalized
          .split("")
          .map((part) => `${part}${part}`)
          .join("")
      : normalized;
  const parsed = Number.parseInt(expanded, 16);
  const rgb =
    expanded.length === 6 && Number.isFinite(parsed)
      ? {
          blue: parsed & 255,
          green: (parsed >> 8) & 255,
          red: (parsed >> 16) & 255,
        }
      : { blue: 218, green: 218, red: 218 };

  COLOR_CACHE.set(color, rgb);
  return rgb;
}

function splitLabel(value: string, maxLength = 13) {
  const text = value.trim();
  if (text.length <= maxLength) return [text];

  const lines: string[] = [];
  let remaining = text;
  for (let index = 0; index < 2 && remaining.length > 0; index += 1) {
    const characters = Array.from(remaining);
    if (characters.length <= maxLength) {
      lines.push(remaining);
      remaining = "";
      break;
    }

    const candidate = characters.slice(0, maxLength).join("");
    const breakAt = candidate.lastIndexOf(" ");
    const line = (breakAt > Math.floor(maxLength * 0.45) ? candidate.slice(0, breakAt) : candidate).trim();
    lines.push(line || candidate);
    remaining = remaining.slice(line.length).trimStart();
  }

  if (remaining.length > 0 && lines.length > 0) {
    const lastLine = Array.from(lines[lines.length - 1]);
    lines[lines.length - 1] = `${lastLine.slice(0, Math.max(1, maxLength - 1)).join("").trimEnd()}…`;
  }

  return lines.slice(0, 2);
}

function normalizePositions(nodes: GraphNode[]) {
  const positions = new Map<string, NormalizedPoint>();
  if (nodes.length === 0) return positions;

  let minX = Number.POSITIVE_INFINITY;
  let minY = Number.POSITIVE_INFINITY;
  let minZ = Number.POSITIVE_INFINITY;
  let maxX = Number.NEGATIVE_INFINITY;
  let maxY = Number.NEGATIVE_INFINITY;
  let maxZ = Number.NEGATIVE_INFINITY;

  for (const node of nodes) {
    minX = Math.min(minX, node.x);
    minY = Math.min(minY, node.y);
    minZ = Math.min(minZ, node.z);
    maxX = Math.max(maxX, node.x);
    maxY = Math.max(maxY, node.y);
    maxZ = Math.max(maxZ, node.z);
  }

  const centerX = (minX + maxX) / 2;
  const centerY = (minY + maxY) / 2;
  const centerZ = (minZ + maxZ) / 2;
  const range = Math.max(maxX - minX, maxY - minY, maxZ - minZ, 1);

  for (const node of nodes) {
    positions.set(node.id, {
      x: ((node.x - centerX) / range) * 2,
      y: ((node.y - centerY) / range) * 2,
      z: ((node.z - centerZ) / range) * 2,
    });
  }
  return positions;
}

function nodeRadius(node: GraphNode, selected: boolean) {
  const importance = normalizedScore(node.importance);
  return (
    clamp(1.8 + importance * 3 + Math.sqrt(Math.max(0, node.degree)) * 0.16, 2.1, 6.2) +
    (selected ? 0.8 : 0)
  );
}

export function GraphCanvas({
  autoRotate,
  edges,
  edgeTypes,
  highlightedEdgeType,
  hasActiveFilters,
  focusNodeIds,
  hasSearch,
  lens,
  nodes,
  nodeTypes,
  origins = [],
  reducedMotion,
  searchMatchIds,
  selectedNodeId,
  viewportCommand,
  onClearEdgeTypeHighlight,
  onSelect,
  onReset,
}: GraphCanvasProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const drawRef = useRef<() => void>(() => undefined);
  const rotationRef = useRef({ ...DEFAULT_ROTATION });
  const zoomRef = useRef(INITIAL_ZOOM);
  const dragRef = useRef<DragState | null>(null);
  const hitMapRef = useRef<ScreenNode[]>([]);
  const [hoveredNodeId, setHoveredNodeId] = useState<string | null>(null);

  const normalizedPositions = useMemo(() => normalizePositions(nodes), [nodes]);
  const typeColorById = useMemo(
    () => new Map(nodeTypes.map((type) => [type.id, type.color] as const)),
    [nodeTypes],
  );
  const edgeColorById = useMemo(
    () => new Map(edgeTypes.map((type) => [type.id, type.color] as const)),
    [edgeTypes],
  );
  const edgeTypeLabelById = useMemo(
    () => new Map(edgeTypes.map((type) => [type.id, type.label] as const)),
    [edgeTypes],
  );
  const originColorById = useMemo(
    () => new Map(origins.map((origin) => [origin.id, origin.color] as const)),
    [origins],
  );
  const nodeById = useMemo(
    () => new Map(nodes.map((node) => [node.id, node] as const)),
    [nodes],
  );

  const getNodeColor = useCallback(
    (node: GraphNode) => {
      if (lens === "label") return "#e9e5dc";
      if (lens === "origin") return originColorById.get(node.origin) ?? "#8fa3b8";
      if (lens === "topic") {
        const topic = node.topics[0] ?? node.type;
        return TOPIC_COLORS[hashString(topic) % TOPIC_COLORS.length];
      }
      return typeColorById.get(node.type) ?? "#8d9290";
    },
    [lens, originColorById, typeColorById],
  );

  const drawScene = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const context = canvas.getContext("2d");
    if (!context) {
      canvas.dataset.renderState = "no-2d-context";
      return;
    }

    const bounds = canvas.getBoundingClientRect();
    const width = bounds.width;
    const height = bounds.height;
    if (width <= 0 || height <= 0) {
      canvas.dataset.renderState = "waiting-for-layout";
      return;
    }

    const pixelRatio = Math.min(window.devicePixelRatio || 1, 2);
    const targetWidth = Math.round(width * pixelRatio);
    const targetHeight = Math.round(height * pixelRatio);
    if (canvas.width !== targetWidth || canvas.height !== targetHeight) {
      canvas.width = targetWidth;
      canvas.height = targetHeight;
    }

    context.setTransform(pixelRatio, 0, 0, pixelRatio, 0, 0);
    context.clearRect(0, 0, width, height);
    context.lineCap = "round";
    context.lineJoin = "round";

    const now = reducedMotion ? 0 : performance.now();
    for (const star of BACKGROUND_STARS) {
      const twinkle = reducedMotion ? 0.72 : 0.55 + 0.45 * Math.sin(now * 0.0006 + star.phase);
      context.beginPath();
      const depth = 0.72 + star.band * 0.28;
      context.fillStyle = `rgba(198, 207, 220, ${star.alpha * depth * twinkle})`;
      context.arc(star.x * width, star.y * height, star.size * (0.86 + star.band * 0.14), 0, Math.PI * 2);
      context.fill();
    }

    const rotation = rotationRef.current;
    const cosY = Math.cos(rotation.y);
    const sinY = Math.sin(rotation.y);
    const cosX = Math.cos(rotation.x);
    const sinX = Math.sin(rotation.x);
    const graphScale = Math.min(width * 0.4, height * 0.34) * zoomRef.current;
    const verticalOffset = height < 620 ? -8 : -12;
    const projected = new Map<string, ScreenNode>();
    const hasSelection = selectedNodeId !== null && normalizedPositions.has(selectedNodeId);
    const hasDesktopDetail = hasSelection && window.innerWidth > 820;
    const detailWidth = hasDesktopDetail
      ? Number.parseFloat(getComputedStyle(canvas).getPropertyValue("--detail-width")) || 480
      : 0;
    const visibleWidth = hasDesktopDetail ? Math.max(0, width - detailWidth) : width;
    const detailShift = hasDesktopDetail ? visibleWidth / 2 - width / 2 : 0;

    for (const node of nodes) {
      const point = normalizedPositions.get(node.id);
      if (!point) continue;

      const rotatedX = point.x * cosY - point.z * sinY;
      const firstZ = point.x * sinY + point.z * cosY;
      const rotatedY = point.y * cosX - firstZ * sinX;
      const rotatedZ = point.y * sinX + firstZ * cosX;
      const perspective = 2.8 / (2.8 + rotatedZ * 0.48);
      const selected = node.id === selectedNodeId;
      let alpha = 0.82 + normalizedScore(node.importance) * 0.18;

      if (hasSelection) alpha = focusNodeIds.has(node.id) ? 1 : 0.095;
      else if (hasSearch) alpha = searchMatchIds.has(node.id) ? 1 : 0.075;

      projected.set(node.id, {
        alpha,
        color: getNodeColor(node),
        node,
        radius: nodeRadius(node, selected) * clamp(perspective, 0.7, 1.3),
        x: width / 2 + detailShift + rotatedX * graphScale * perspective,
        y: height / 2 + verticalOffset + rotatedY * graphScale * perspective,
        z: rotatedZ,
      });
    }

    for (const edge of edges) {
      const source = projected.get(edge.source);
      const target = projected.get(edge.target);
      if (!source || !target) continue;

      const directConnection =
        selectedNodeId !== null && (edge.source === selectedNodeId || edge.target === selectedNodeId);
      const searchConnection = searchMatchIds.has(edge.source) && searchMatchIds.has(edge.target);
      const edgeTypeHighlighted =
        highlightedEdgeType !== null && edge.type === highlightedEdgeType;
      const style = EDGE_STYLES[edge.type] ?? DEFAULT_EDGE_STYLE;
      const weight = clamp(edge.weight || 0.5, 0.25, 1);
      let alpha = AMBIENT_EDGE_ALPHA * weight;
      let widthValue = style.width * (0.82 + weight * 0.18);
      let strokeColor = NEUTRAL_EDGE_COLOR;

      if (highlightedEdgeType !== null) {
        alpha = edgeTypeHighlighted ? (directConnection ? 0.98 : 0.74) : 0.12;
        widthValue = edgeTypeHighlighted
          ? style.width * (directConnection ? 1.62 : 1.28)
          : style.width * 0.82;
        strokeColor = edgeTypeHighlighted
          ? edgeColorById.get(edge.type) ?? source.color
          : NEUTRAL_EDGE_COLOR;
      } else if (hasSelection) {
        alpha = directConnection ? 0.68 : 0.02;
        widthValue = directConnection ? style.width * 1.12 : style.width * 0.72;
        strokeColor = directConnection
          ? edgeColorById.get(edge.type) ?? source.color
          : NEUTRAL_EDGE_COLOR;
      } else if (hasSearch) {
        alpha = searchConnection ? 0.34 : 0.02;
      }

      context.beginPath();
      context.moveTo(source.x, source.y);
      context.lineTo(target.x, target.y);
      context.globalCompositeOperation = "source-over";
      context.setLineDash(style.dash);

      if (edgeTypeHighlighted) {
        context.globalCompositeOperation = "lighter";
        context.globalAlpha = (directConnection ? 0.3 : 0.19) * alpha;
        context.strokeStyle = strokeColor;
        context.lineWidth = widthValue * (directConnection ? 2.8 : 2.25);
        context.stroke();
        context.globalCompositeOperation = "source-over";
      }

      context.globalAlpha = alpha;
      context.strokeStyle = strokeColor;
      context.lineWidth = widthValue;
      context.setLineDash(style.dash);
      context.stroke();

      if (edgeTypeHighlighted) {
        context.globalAlpha = directConnection ? 0.95 : 0.82;
        context.strokeStyle = directConnection ? "#fff6e2" : strokeColor;
        context.lineWidth = Math.max(0.42, widthValue * (directConnection ? 0.34 : 0.28));
        context.setLineDash(style.dash);
        context.stroke();
      }
    }
    context.setLineDash([]);
    context.globalAlpha = 1;
    context.globalCompositeOperation = "source-over";

    const screenNodes = Array.from(projected.values()).sort((left, right) => right.z - left.z);
    for (const screenNode of screenNodes) {
      const { alpha, color, node, radius, x, y } = screenNode;
      const selected = node.id === selectedNodeId;
      const hovered = node.id === hoveredNodeId;
      const importance = normalizedScore(node.importance);
      const rgb = colorToRgb(color);
      const nodePhase = (hashString(node.id) % 628) / 100;
      const twinkle = reducedMotion ? 1 : 0.94 + Math.sin(now * 0.001 + nodePhase) * 0.06;
      const glowStrength = selected
        ? 1.25
        : hovered
          ? 1.08
          : STAR_GLOW.ambientStrength + importance * 0.26;

      context.globalCompositeOperation = "lighter";
      if (alpha > 0.08) {
        const glowRadius =
          radius *
          (selected
            ? STAR_GLOW.selectedRadius
            : hovered
              ? STAR_GLOW.ambientRadius
              : STAR_GLOW.ambientRadius + importance * 1.8);
        const glow = context.createRadialGradient(x, y, 0, x, y, glowRadius);
        glow.addColorStop(
          0,
          `rgba(${rgb.red}, ${rgb.green}, ${rgb.blue}, ${0.52 * alpha * glowStrength * twinkle})`,
        );
        glow.addColorStop(
          0.2,
          `rgba(${rgb.red}, ${rgb.green}, ${rgb.blue}, ${0.24 * alpha * glowStrength * twinkle})`,
        );
        glow.addColorStop(
          0.52,
          `rgba(${rgb.red}, ${rgb.green}, ${rgb.blue}, ${0.07 * alpha * glowStrength})`,
        );
        glow.addColorStop(0.78, `rgba(${rgb.red}, ${rgb.green}, ${rgb.blue}, 0.015)`);
        glow.addColorStop(1, "rgba(0, 0, 0, 0)");
        context.beginPath();
        context.arc(x, y, glowRadius, 0, Math.PI * 2);
        context.fillStyle = glow;
        context.globalAlpha = 1;
        context.fill();
      }

      if ((selected || hovered || importance >= 0.82) && alpha > 0.32) {
        const rayLength = radius * (selected ? 3.8 : hovered ? 2.7 : 2.15);
        context.beginPath();
        context.moveTo(x - rayLength, y);
        context.lineTo(x + rayLength, y);
        context.moveTo(x, y - rayLength);
        context.lineTo(x, y + rayLength);
        context.strokeStyle = color;
        context.globalAlpha = alpha * (selected ? 0.34 : hovered ? 0.24 : 0.12) * twinkle;
        context.lineWidth = 0.65;
        context.stroke();
      }

      // Build the node as a point of light instead of a shaded sphere. Additive
      // compositing lets the halo carry the visual weight while the core fades
      // out before it can form a hard circular edge.
      const coreRadius = radius * STAR_GLOW.coreRadius;
      const core = context.createRadialGradient(x, y, 0, x, y, coreRadius);
      core.addColorStop(0, `rgba(255, 255, 255, ${0.78 * alpha * twinkle})`);
      core.addColorStop(0.18, `rgba(255, 255, 255, ${0.36 * alpha * twinkle})`);
      core.addColorStop(0.46, `rgba(${rgb.red}, ${rgb.green}, ${rgb.blue}, ${0.72 * alpha * twinkle})`);
      core.addColorStop(0.8, `rgba(${rgb.red}, ${rgb.green}, ${rgb.blue}, ${0.16 * alpha * twinkle})`);
      core.addColorStop(1, "rgba(0, 0, 0, 0)");
      context.beginPath();
      context.arc(x, y, coreRadius, 0, Math.PI * 2);
      context.fillStyle = core;
      context.globalAlpha = 1;
      context.fill();

      context.globalAlpha = 1;
      context.globalCompositeOperation = "source-over";
    }

    const labelPriority = (screenNode: ScreenNode) => {
      if (screenNode.node.id === selectedNodeId) return 10_000;
      if (screenNode.node.id === hoveredNodeId) return 9_000;
      return screenNode.node.degree * 100 + screenNode.node.importance;
    };
    const labelCandidates = [...screenNodes].sort(
      (left, right) => labelPriority(right) - labelPriority(left),
    );
    const labelBoxes: Array<{ bottom: number; left: number; right: number; top: number }> = [];
    const ambientLabelLimit = width <= 640 ? MAX_MOBILE_AMBIENT_LABELS : MAX_AMBIENT_LABELS;
    let ambientLabelCount = 0;

    for (const screenNode of labelCandidates) {
      const { alpha, color, node, radius, x, y } = screenNode;
      const selected = node.id === selectedNodeId;
      const hovered = node.id === hoveredNodeId;
      const contextualLabel = selected || hovered;
      const ambientLabel = node.degree >= 6 && normalizedScore(node.importance) >= 0.6;
      const showLabel = lens === "label" || contextualLabel || ambientLabel;
      if (!showLabel || alpha < 0.15) continue;
      if (!contextualLabel && lens !== "label" && ambientLabelCount >= ambientLabelLimit) continue;

      const lines = splitLabel(node.title, selected ? 15 : 12);
      const fontSize = selected ? 14 : hovered ? 12 : 10.5;
      const lineHeight = fontSize + 3;
      let labelX = x + radius + (selected ? 10 : 7);
      const labelY = y - ((lines.length - 1) * lineHeight) / 2;
      context.font = `${selected ? 650 : 500} ${fontSize}px Inter, Pretendard, system-ui, sans-serif`;
      context.textBaseline = "middle";

      const labelWidth = Math.max(...lines.map((line) => context.measureText(line).width));
      if (labelX + labelWidth + 10 > visibleWidth - 12) {
        labelX = Math.max(12, x - radius - labelWidth - (selected ? 16 : 12));
      }
      const labelBox = {
        left: labelX - 6,
        right: labelX + labelWidth + 10,
        top: labelY - lineHeight / 2 - 4,
        bottom: labelY - lineHeight / 2 - 4 + lineHeight * lines.length + 8,
      };
      const overlapsLabel = labelBoxes.some(
        (box) =>
          labelBox.left < box.right + 8 &&
          labelBox.right + 8 > box.left &&
          labelBox.top < box.bottom + 6 &&
          labelBox.bottom + 6 > box.top,
      );
      const overlapsChrome = labelBox.top < 92 || labelBox.bottom > height - 148;
      if (!selected && !hovered && (overlapsLabel || overlapsChrome)) continue;
      labelBoxes.push(labelBox);
      if (!contextualLabel && lens !== "label") ambientLabelCount += 1;

      const labelBandStrength = milkyWayStrength(
        clamp((labelBox.left + labelBox.right) / (2 * width), 0, 1),
        clamp((labelBox.top + labelBox.bottom) / (2 * height), 0, 1),
      );
      const needsBackdrop = contextualLabel || labelBandStrength > 0.5;
      if (needsBackdrop) {
        context.fillStyle = contextualLabel ? "#0c0e0d" : "#080c14";
        context.globalAlpha = contextualLabel ? 0.92 : 0.32 + labelBandStrength * 0.22;
        context.fillRect(labelX - 6, labelY - lineHeight / 2 - 4, labelWidth + 16, lineHeight * lines.length + 8);
        context.strokeStyle = contextualLabel ? "#303432" : "rgba(160, 178, 204, 0.14)";
        context.globalAlpha = contextualLabel ? 0.9 : 0.45;
        context.lineWidth = 0.7;
        context.strokeRect(labelX - 6, labelY - lineHeight / 2 - 4, labelWidth + 16, lineHeight * lines.length + 8);
      }

      context.fillStyle = selected ? "#f4f0e8" : "#d2d0ca";
      context.globalAlpha = selected ? 1 : Math.max(0.55, alpha * 0.9);
      lines.forEach((line, index) => context.fillText(line, labelX, labelY + index * lineHeight));
      context.fillStyle = color;
      context.globalAlpha = alpha;
      context.fillRect(labelX - 2, labelY - lineHeight / 2, 1.5, lineHeight * lines.length - 3);
    }

    context.globalAlpha = 1;
    hitMapRef.current = screenNodes;
    canvas.dataset.renderState = `drawn-${screenNodes.length}`;
  }, [
    edgeColorById,
    edges,
    focusNodeIds,
    getNodeColor,
    hasSearch,
    highlightedEdgeType,
    hoveredNodeId,
    lens,
    nodes,
    normalizedPositions,
    reducedMotion,
    searchMatchIds,
    selectedNodeId,
  ]);

  // Keep the latest renderer available during the commit phase so the callback
  // ref can schedule the first paint even when the initial layout starts at 0px.
  drawRef.current = drawScene;

  useEffect(() => {
    drawRef.current = drawScene;
    drawScene();
  }, [drawScene]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    let animationFrame = 0;
    const requestDraw = () => {
      cancelAnimationFrame(animationFrame);
      animationFrame = requestAnimationFrame(() => drawRef.current());
    };

    const observer = new ResizeObserver(requestDraw);
    observer.observe(canvas.parentElement ?? canvas);
    window.addEventListener("resize", requestDraw);
    requestDraw();

    return () => {
      cancelAnimationFrame(animationFrame);
      observer.disconnect();
      window.removeEventListener("resize", requestDraw);
    };
  }, []);

  useEffect(() => {
    if (reducedMotion) return;

    let animationFrame = 0;
    let previousTime = performance.now();
    let previousDrawTime = 0;
    const animate = (time: number) => {
      const elapsed = Math.min(48, time - previousTime);
      previousTime = time;
      if (autoRotate && !dragRef.current) rotationRef.current.y += elapsed * 0.000055;
      if (time - previousDrawTime >= COSMIC_FRAME_INTERVAL) {
        previousDrawTime = time;
        drawRef.current();
      }
      animationFrame = requestAnimationFrame(animate);
    };

    animationFrame = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(animationFrame);
  }, [autoRotate, reducedMotion]);

  useEffect(() => {
    if (viewportCommand.action === "zoom-in") {
      zoomRef.current = clamp(zoomRef.current * 1.16, MIN_ZOOM, MAX_ZOOM);
    } else if (viewportCommand.action === "zoom-out") {
      zoomRef.current = clamp(zoomRef.current / 1.16, MIN_ZOOM, MAX_ZOOM);
    } else {
      rotationRef.current = { ...DEFAULT_ROTATION };
      zoomRef.current = INITIAL_ZOOM;
    }
    drawRef.current();
  }, [viewportCommand]);

  const findNodeAt = (clientX: number, clientY: number) => {
    const canvas = canvasRef.current;
    if (!canvas) return null;
    const bounds = canvas.getBoundingClientRect();
    const x = clientX - bounds.left;
    const y = clientY - bounds.top;

    for (let index = hitMapRef.current.length - 1; index >= 0; index -= 1) {
      const node = hitMapRef.current[index];
      const distance = Math.hypot(node.x - x, node.y - y);
      if (distance <= Math.max(12, node.radius + 6)) return node.node.id;
    }
    return null;
  };

  const highlightedEdgeLabel = highlightedEdgeType
    ? edgeTypeLabelById.get(highlightedEdgeType) ?? highlightedEdgeType
    : null;
  const hoveredNode = hoveredNodeId ? nodeById.get(hoveredNodeId) : null;

  return (
    <div className="canvas-wrap">
      <canvas
        aria-describedby="graph-instructions"
        aria-label={`생각 그래프. ${nodes.length}개 노드. 화살표 키와 Home, End 키로 노드를 선택할 수 있습니다.${
          hoveredNode ? ` 현재 가리킨 노드: ${hoveredNode.title}.` : ""
        } ${
          highlightedEdgeLabel
            ? `${highlightedEdgeLabel} 엣지 유형이 강조되어 있습니다. Escape 키로 강조를 해제할 수 있습니다.`
            : "Escape 키로 선택을 해제할 수 있습니다."
        }`}
        className={hoveredNodeId ? "is-node-hovered" : ""}
        height={900}
        onKeyDown={(event) => {
          if (event.key === "Escape") {
            event.preventDefault();
            if (highlightedEdgeType !== null) onClearEdgeTypeHighlight();
            else onSelect(null);
            return;
          }

          if (nodes.length === 0) return;
          const currentIndex = selectedNodeId
            ? nodes.findIndex((node) => node.id === selectedNodeId)
            : -1;
          let nextIndex = currentIndex;

          if (event.key === "ArrowRight" || event.key === "ArrowDown") {
            nextIndex = (currentIndex + 1 + nodes.length) % nodes.length;
          } else if (event.key === "ArrowLeft" || event.key === "ArrowUp") {
            nextIndex =
              currentIndex < 0 ? nodes.length - 1 : (currentIndex - 1 + nodes.length) % nodes.length;
          } else if (event.key === "Home") {
            nextIndex = 0;
          } else if (event.key === "End") {
            nextIndex = nodes.length - 1;
          } else if (event.key === "Enter" && currentIndex < 0) {
            nextIndex = 0;
          } else {
            return;
          }

          event.preventDefault();
          const nextNode = nodes[nextIndex];
          if (nextNode) onSelect(nextNode.id);
        }}
        onPointerDown={(event) => {
          event.currentTarget.setPointerCapture(event.pointerId);
          dragRef.current = {
            lastX: event.clientX,
            lastY: event.clientY,
            moved: false,
            pointerId: event.pointerId,
            startX: event.clientX,
            startY: event.clientY,
          };
        }}
        onLostPointerCapture={(event) => {
          if (dragRef.current?.pointerId === event.pointerId) dragRef.current = null;
          setHoveredNodeId(null);
        }}
        onPointerCancel={(event) => {
          if (dragRef.current?.pointerId === event.pointerId) dragRef.current = null;
          setHoveredNodeId(null);
        }}
        onPointerLeave={() => {
          if (!dragRef.current) setHoveredNodeId(null);
        }}
        onPointerMove={(event) => {
          const drag = dragRef.current;
          if (drag) {
            const deltaX = event.clientX - drag.lastX;
            const deltaY = event.clientY - drag.lastY;
            drag.lastX = event.clientX;
            drag.lastY = event.clientY;
            drag.moved ||= Math.hypot(event.clientX - drag.startX, event.clientY - drag.startY) > 4;
            rotationRef.current.y += deltaX * 0.006;
            rotationRef.current.x = clamp(rotationRef.current.x + deltaY * 0.004, -1.1, 1.1);
            drawRef.current();
            return;
          }

          setHoveredNodeId(findNodeAt(event.clientX, event.clientY));
        }}
        onPointerUp={(event) => {
          const drag = dragRef.current;
          if (!drag || drag.pointerId !== event.pointerId) return;
          if (!drag.moved) onSelect(findNodeAt(event.clientX, event.clientY));
          dragRef.current = null;
          event.currentTarget.releasePointerCapture(event.pointerId);
        }}
        onWheel={(event) => {
          event.preventDefault();
          const factor = event.deltaY > 0 ? 0.92 : 1.08;
          zoomRef.current = clamp(zoomRef.current * factor, MIN_ZOOM, MAX_ZOOM);
          drawRef.current();
        }}
        role="img"
        tabIndex={0}
        title={hoveredNode?.title}
        ref={(canvas) => {
          canvasRef.current = canvas;
          if (canvas) requestAnimationFrame(() => drawRef.current());
        }}
        width={1200}
      />
      {nodes.length === 0 ? (
        <div className="graph-empty" role="status">
          <span>{hasActiveFilters ? "현재 필터와 일치하는 노드가 없습니다." : "표시할 공개 노드가 없습니다."}</span>
          <small>
            {hasActiveFilters ? "검색어나 사이드바 필터를 조정해 보세요." : "공개된 지식이 준비되면 이곳에 표시됩니다."}
          </small>
          {hasActiveFilters ? (
            <button onClick={onReset} type="button">
              모든 필터 해제
            </button>
          ) : null}
        </div>
      ) : null}
      <p className="sr-only" aria-live="polite">
        {highlightedEdgeLabel
          ? `${highlightedEdgeLabel} 엣지 유형 강조됨. 다시 누르거나 Escape로 해제할 수 있습니다.`
          : selectedNodeId
            ? `${nodeById.get(selectedNodeId)?.title ?? "노드"} 선택됨`
            : ""}
      </p>
    </div>
  );
}
